import { AUTH_TOKEN, AUTH_USER, AUTH_DATE } from '../redux/constants/Auth';

export const SESSION_EXPIRED_URL = '/auth/login?reason=session-expired';
export const sessionToken = () => localStorage.getItem(AUTH_TOKEN);
const rawToken = token => (token || '').replace(/^Bearer\s+/i, '');

// Used only to schedule local logout. The server remains responsible for verification.
export function tokenExpiresAt(token) {
  try {
    const part = rawToken(token).split('.')[1];
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && Number.isFinite(payload.exp) ? payload.exp * 1000 : null;
  } catch { return null; }
}

export function createSessionLifecycle({ getToken, clear, redirect, now = Date.now }) {
  let ended = false;
  let current = null;
  let timer;
  const listeners = new Set();
  const notify = () => listeners.forEach(listener => listener());
  const expire = token => {
    // An old response must never terminate a newer login.
    if (ended || (token && rawToken(token) !== rawToken(getToken()))) return false;
    ended = true;
    clearTimeout(timer);
    clear();
    notify();
    redirect(SESSION_EXPIRED_URL);
    return true;
  };
  const check = () => {
    const token = getToken();
    if (token && token !== current) { current = token; ended = false; }
    clearTimeout(timer);
    const expiresAt = tokenExpiresAt(token);
    if (token && expiresAt !== null) {
      if (expiresAt <= now()) { expire(token); return false; }
      timer = setTimeout(check, Math.min(expiresAt - now(), 2147483647));
    }
    return Boolean(token) && !ended;
  };
  return {
    check, expire,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    stop() { clearTimeout(timer); },
  };
}

export const sessionLifecycle = createSessionLifecycle({
  getToken: sessionToken,
  clear: () => [AUTH_TOKEN, AUTH_USER, AUTH_DATE].forEach(key => localStorage.removeItem(key)),
  redirect: path => window.location.replace(path),
});

export function startSessionMonitor() {
  const check = () => sessionLifecycle.check();
  const storage = event => {
    if (event.key === AUTH_TOKEN || event.key === null) {
      if (!sessionToken() && window.location.pathname.startsWith('/app')) sessionLifecycle.expire();
      else check();
    }
  };
  check();
  window.addEventListener('focus', check);
  window.addEventListener('storage', storage);
  document.addEventListener('visibilitychange', check);
  return () => {
    sessionLifecycle.stop();
    window.removeEventListener('focus', check);
    window.removeEventListener('storage', storage);
    document.removeEventListener('visibilitychange', check);
  };
}
